/**
 * useUndoableState.ts - Generic hook for state with automatic undo/redo
 * 
 * Provides a drop-in replacement for useState that automatically handles:
 * - Snapshot capture before changes
 * - Change recording to backtrackr
 * - State restoration on undo/redo
 * - Loop prevention
 * - Context management
 * 
 * @example
 * ```typescript
 * const [state, setState] = useUndoableState(
 *   { nodes: [], expanded: new Set() },
 *   'outliner:view:123',
 *   backtrackr
 * );
 * 
 * setState(
 *   prev => ({ ...prev, nodes: [...prev.nodes, newNode] }),
 *   'Add node'
 * );
 * ```
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import type { BacktrackrContextValue } from '@opcentral/backtrackr';

/**
 * Generic hook for component state with automatic undo/redo
 * 
 * @param initialState - Initial state value (any type)
 * @param contextId - Unique context ID for this state (e.g., "outliner:view:123")
 * @param backtrackr - Backtrackr instance from useBacktrackr()
 * @returns [state, setState] tuple where setState requires a description
 */
export function useUndoableState<T>(
  initialState: T,
  contextId: string,
  backtrackr: BacktrackrContextValue | null
): readonly [
  state: T,
  setState: (updater: T | ((prev: T) => T), description: string) => void
] {
  const [state, setState] = useState<T>(initialState);
  const isRestoringRef = useRef(false);
  const stateRef = useRef<T>(state);
  const backtrackrRef = useRef(backtrackr);

  // Keep refs in sync
  useEffect(() => {
    stateRef.current = state;
  }, [state]);
  
  useEffect(() => {
    backtrackrRef.current = backtrackr;
  }, [backtrackr]);
  
  // Setup undo/redo listener - only run once on mount
  useEffect(() => {
    const bt = backtrackrRef.current;
    if (!bt?.historyManager) {
      console.warn('[useUndoableState] Backtrackr not available for context:', contextId);
      return;
    }
    
    // console.log('[useUndoableState] Setting up undo/redo for context:', contextId);
    
    // Set active context
    bt.historyManager.setActiveContext(contextId);
    
    // Listen for undo/redo events
    const unsubscribe = bt.historyManager.addListener((event: string, data: any) => {
      // Ignore non-undo/redo events
      if (event !== 'undo' && event !== 'redo') {
        return;
      }
      
      // console.log('[useUndoableState] Received event:', event, 'for context:', data.contextId);
      
      // Only handle events for our context
      if (data.contextId && data.contextId !== contextId) {
        // console.log('[useUndoableState] Ignoring event for different context');
        return;
      }
      
      // Get state to restore
      const newState = event === 'undo' ? data.revertState : data.redoState;
      
      if (!newState) {
        console.warn('[useUndoableState] No state to restore');
        return;
      }
      
      // console.log('[useUndoableState] Restoring state');
      
      // Set flag to prevent recording this restoration
      isRestoringRef.current = true;
      
      // Restore state
      setState(newState);
      
      // Clear flag after React finishes updating
      Promise.resolve().then(() => {
        isRestoringRef.current = false;
        // console.log('[useUndoableState] State restoration complete');
      });
    });
    
    // Cleanup - only unsubscribe
    return () => {
      // console.log('[useUndoableState] Cleaning up context:', contextId);
      unsubscribe();
    };
  }, [contextId]); // Only depend on contextId, not backtrackr!
  
  // Enhanced setState with undo recording
  const setStateWithUndo = useCallback((
    updater: T | ((prev: T) => T),
    description: string
  ) => {
    // Don't record during restoration
    if (isRestoringRef.current) {
      // console.log('[useUndoableState] Skipping recording - restoring state');
      return;
    }
    
    // Check if this should skip undo recording (description contains "no undo")
    const skipUndo = description.toLowerCase().includes('no undo');
    
    const bt = backtrackrRef.current;
    if (!bt?.recordChange || skipUndo) {
      if (skipUndo) {
        // console.log('[useUndoableState] Skipping undo recording:', description);
      } else {
        console.warn('[useUndoableState] Backtrackr not available, state will update but not be recorded');
      }
      setState(updater);
      return;
    }
    
    if (!description) {
      console.warn('[useUndoableState] No description provided for change');
    }
    
    // Capture before state
    const before = stateRef.current;
    
    // Update state
    setState(prev => {
      const next = typeof updater === 'function' ? (updater as (prev: T) => T)(prev) : updater;
      
      // Record change after state updates (async to ensure state is updated)
      requestAnimationFrame(() => {
        // Double-check we're not restoring
        if (isRestoringRef.current) {
          // console.log('[useUndoableState] Skipping recording - restoring state (delayed check)');
          return;
        }
        
        // console.log('[useUndoableState] Recording change:', description, 'context:', contextId);
        
        try {
          bt.recordChange({
            type: 'change',
            description,
            context: contextId,
            before,  // Backtrackr converts this to revertState
            after: next  // Backtrackr converts this to redoState
          });
          // console.log('[useUndoableState] Change recorded successfully');
        } catch (error) {
          console.error('[useUndoableState] Error recording change:', error);
        }
      });
      
      return next;
    });
  }, [contextId]); // Only depend on contextId
  
  return [state, setStateWithUndo] as const;
}
